import { getBrandingProperty, getThemeFromBranding, shouldUseHandyStyle } from "config/brandConfigNew";
import { useBranding } from "contexts/BrandingContext";
import { useCallback } from "react";

/**
 * Hook personalizado para manejar el branding de white labels
 * Reemplaza las funciones deprecadas del sistema anterior
 */
export const useWhiteLabelBranding = () => {
  const { branding, loading, error, refreshBranding, getBrandName, getBrandColor, getBrandResource } = useBranding();

  /**
   * Determina si se debe usar el estilo Handy
   * Reemplaza: TIENDA_HANDY
   */
  const isHandyStyle = useCallback(() => {
    return shouldUseHandyStyle(branding);
  }, [branding]);

  /**
   * Obtiene el radio de borde basado en el branding
   * Reemplaza: TIENDA_HANDY ? 0 : "16px"
   */
  const getBorderRadius = useCallback(
    (defaultRadius = "16px") => {
      if (branding?.branding?.theme?.buttons?.radius !== undefined) {
        return `${branding.branding.theme.buttons.radius}px`;
      }
      return isHandyStyle() ? "0px" : defaultRadius;
    },
    [branding, isHandyStyle]
  );

  /**
   * Obtiene la variante de input basada en el branding
   * Reemplaza: TIENDA_HANDY ? "standard" : "outlined"
   */
  const getInputVariant = useCallback(() => {
    return isHandyStyle() ? "standard" : "outlined";
  }, [isHandyStyle]);

  /**
   * Obtiene la configuración de tema completa
   */
  const getThemeConfig = useCallback(() => {
    return getThemeFromBranding(branding);
  }, [branding]);

  /**
   * Obtiene una propiedad específica del branding
   */
  const getBrandProperty = useCallback(
    (property, fallback = null) => {
      return getBrandingProperty(branding, property, fallback);
    },
    [branding]
  );

  /**
   * Obtiene el color primario del branding
   */
  const getPrimaryColor = useCallback(() => {
    return getBrandColor("primary") || "#2296f3";
  }, [getBrandColor]);

  /**
   * Obtiene el color secundario del branding
   */
  const getSecondaryColor = useCallback(() => {
    return getBrandColor("secondary") || "#f50057";
  }, [getBrandColor]);

  /**
   * Obtiene la URL del logo
   */
  const getLogoUrl = useCallback(() => {
    return getBrandResource("logo") || "/favicon.png";
  }, [getBrandResource]);

  /**
   * Obtiene la URL del favicon
   */
  const getFaviconUrl = useCallback(() => {
    return getBrandResource("favicon") || "/favicon.png";
  }, [getBrandResource]);

  /**
   * Verifica si el branding está listo
   */
  const isBrandingReady = useCallback(() => {
    return !loading && branding !== null;
  }, [loading, branding]);

  /**
   * Obtiene información del white label actual
   */
  const getWhiteLabelInfo = useCallback(() => {
    return (
      branding?.white_label || {
        id: null,
        name: "Rhino",
        slug: "rhino",
      }
    );
  }, [branding]);

  /**
   * Aplica estilos condicionales basados en el branding
   */
  const getConditionalStyles = useCallback(
    (handyStyles = {}, defaultStyles = {}) => {
      return isHandyStyle() ? handyStyles : defaultStyles;
    },
    [isHandyStyle]
  );

  /**
   * Helper para compatibilidad con Material-UI sx prop
   */
  const createSxProp = useCallback(
    (baseSx = {}, handySx = {}) => {
      return {
        ...baseSx,
        ...(isHandyStyle() ? handySx : {}),
      };
    },
    [isHandyStyle]
  );

  return {
    // Estado del branding
    branding,
    loading,
    error,
    isBrandingReady: isBrandingReady(),

    // Información del white label
    whiteLabelInfo: getWhiteLabelInfo(),
    brandName: getBrandName(),

    // Funciones de estilo
    isHandyStyle: isHandyStyle(),
    getBorderRadius,
    getInputVariant,
    getConditionalStyles,
    createSxProp,

    // Configuración de tema
    themeConfig: getThemeConfig(),

    // Colores y recursos
    primaryColor: getPrimaryColor(),
    secondaryColor: getSecondaryColor(),
    logoUrl: getLogoUrl(),
    faviconUrl: getFaviconUrl(),

    // Funciones utilitarias
    getBrandProperty,
    refreshBranding,

    // Funciones de compatibilidad (deprecated)
    getCurrentBrandConfig: () => branding,
    getBrandUrl: () => branding?.white_label?.url || "https://rhinox.io/",
    getBrandSiteName: () => branding?.white_label?.siteName || "rhinox.io",
  };
};

/**
 * Hook simplificado para obtener solo el estado del estilo Handy
 * Reemplaza el uso directo de TIENDA_HANDY
 */
export const useIsHandyStyle = () => {
  const { branding } = useBranding();
  return shouldUseHandyStyle(branding);
};

/**
 * Hook para obtener configuración de tema dinámicamente
 */
export const useDynamicTheme = () => {
  const { branding } = useBranding();
  return getThemeFromBranding(branding);
};

export default useWhiteLabelBranding;
